import { useContext, useEffect, useMemo, useState } from "react";

import { LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";

import { Legends } from "app/components/React/Legends/Legends";
import { CHART_COLORS } from "app/constants/ChartColors";
import type { ChannelKeyName, MetricKeyNames, SegmentId } from "app/constants/MmxChannels";
import { CHANNEL_KEY_NAMES } from "app/constants/MmxChannels";
import type { IGranularityDesc } from "app/pages/segments/mmx/MMXCommon";
import type { ISegmentsMMXGraphData } from "app/pages/segments/mmx/mmxGraphData";
import { SegmentsSingleGraphLegendsContext } from "app/pages/segments/mmx/SegmentsSingleGraphLegendsContext";
import { StyledLegendWrapper } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/StyledComponents";
import { allTrackers } from "app/services/track/track";

import { EngagementVerticals } from "./EngagementVerticals";
import { SegmentsMmxChannelsMapping } from "./SegmentsMarketingChannelsConfig";
import { HorizontalLegendWrapper } from "./styledComponents";

interface ISegmentChannelsLegendProps {
    selectedDisplayTypeIndex: number;
    data: ISegmentsMMXGraphData;
    selectedMetric: MetricKeyNames;
    selectedGranularity: IGranularityDesc;
    id: SegmentId;
    orientation?: "vertical" | "horizontal";
}

export const SegmentChannelsLegends = (props: ISegmentChannelsLegendProps) => {
    const { selectedDisplayTypeIndex, data, selectedMetric, selectedGranularity, id, orientation = "vertical" } = props;
    const isHorizontal = orientation === "horizontal";
    const { hiddenLegends, setHiddenLegends } = useContext(SegmentsSingleGraphLegendsContext);
    const augmentedChannels = SegmentsMmxChannelsMapping.map(({ dataKey, display }) => {
        return {
            key: dataKey,
            name: display,
            hidden: hiddenLegends?.length > 0 && hiddenLegends.includes(dataKey),
            color: CHART_COLORS.trafficSourcesColorsBySourceMMX[dataKey],
        };
    });
    const totals = useMemo(() => {
        const graphData = data?.[id]?.[selectedGranularity.value]?.[selectedMetric];
        const total = CHANNEL_KEY_NAMES.reduce(
            (acc, key) => {
                acc[key] = graphData?.[key]?.Total?.Value ?? 0;
                return acc;
            },
            {} as Record<ChannelKeyName, number>,
        );
        const allChannelsTotal = Object.values(total).reduce((acc, channelTotal) => acc + channelTotal, 0);
        return { total, allChannelsTotal };
    }, [selectedMetric, selectedDisplayTypeIndex, data, id, selectedGranularity.value]);

    const getLegendItems = (selectedMetric: MetricKeyNames) => {
        const isPercentage = selectedMetric === EngagementVerticals.Visits.name && selectedDisplayTypeIndex === 1;
        return augmentedChannels.map((augChannel) => {
            const totalValue =
                selectedMetric !== EngagementVerticals.Duration.dataKey && totals.total[augChannel.key] === 0
                    ? null
                    : totals.total[augChannel.key];
            const value =
                totalValue && isPercentage
                    ? EngagementVerticals[selectedMetric].percentFilter[0]()(totalValue / totals.allChannelsTotal, 2)
                    : EngagementVerticals[selectedMetric].filter[0]()(
                          totalValue,
                          selectedMetric === EngagementVerticals.Duration.dataKey ? null : 2,
                      );
            return {
                key: augChannel.key,
                data: value,
                name: augChannel.name,
                hidden: augChannel.hidden,
                color: augChannel.color,
                isWinner: false,
            };
        });
    };
    const [legendItems, setLegendItems] = useState(() => {
        return getLegendItems(selectedMetric);
    });
    useEffect(() => {
        setLegendItems(getLegendItems(selectedMetric));
    }, [selectedMetric, selectedDisplayTypeIndex, data, id, selectedGranularity.value]);
    const onLegendClick = (filter: any) => {
        const action = filter.hidden ? "add" : "remove";
        let isAllLegendsChecked = true;
        const filteredChannels: any[] = [];
        const filterChannels = legendItems?.map((f) => {
            if (f.key === filter.key) {
                f.hidden = !f.hidden;
            }
            if (f.hidden) {
                isAllLegendsChecked = false;
                filteredChannels.push(f.key);
            }
            return f;
        });
        setLegendItems(filterChannels);
        setHiddenLegends(filteredChannels);
        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `${selectedMetric || ""}/${filter.name || ""}/${action || ""}`,
        );
    };
    return (
        <Legends
            legendComponent={LegendWithOneLineCheckboxFlex}
            legendComponentWrapper={isHorizontal ? HorizontalLegendWrapper : StyledLegendWrapper}
            legendItems={legendItems}
            toggleSeries={onLegendClick}
            gridDirection={isHorizontal ? "row" : "column"}
            textMaxWidth={"125px"}
        />
    );
};
